import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { api } from './account.ts';
import { associateGuestPlan, lastAccountId, rememberAccountId } from './db.ts';
import { withSessionLock } from './session-lock.ts';
import { useLocation, useNavigate } from 'react-router-dom';
import { safeReturnPath } from './return-path.ts';
import type { AccountUser, ApiResult, SessionState } from './account.ts';

interface SessionContextValue {
  session: SessionState | null;
  known: boolean;
  refresh(): Promise<SessionState | null>;
  trusted: boolean;
  storageUserId: string | null;
  authenticate<T extends { user?: AccountUser; recoveryCodes?: string[] }>(
    work: () => Promise<ApiResult<T>>,
    options?: { signup?: boolean },
  ): Promise<ApiResult<T>>;
  logout(): Promise<ApiResult<{ ok: boolean }>>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionState | null>(null);
  const [known, setKnown] = useState(false);
  const [storageUserId, setStorageUserId] = useState(lastAccountId);
  const requestVersion = useRef(0);
  const mutating = useRef(false);
  const [trusted, setTrusted] = useState(false);
  const [pending, setPending] = useState<{ user: AccountUser; recoveryCodes: string[] } | null>(null);
  const pendingRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const channel = useRef<BroadcastChannel | null>(null);

  const refresh = useCallback(async () => {
    if (mutating.current || pendingRef.current) return null;
    const version = ++requestVersion.current;
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), 5000);
    const result = await api.session(abort.signal);
    clearTimeout(timeout);
    if (version !== requestVersion.current) return null;
    if (result.ok && result.data) {
      setSession(result.data);
      const id = result.data.user?.id ?? null;
      rememberAccountId(id);
      setStorageUserId(id);
    } else if (result.status === 401) {
      setSession({ user: null });
      rememberAccountId(null);
      setStorageUserId(null);
    }
    setTrusted(result.ok && Boolean(result.data));
    setKnown(true);
    return result.data;
  }, []);

  const authenticate = useCallback(async <T extends { user?: AccountUser; recoveryCodes?: string[] },>(
    work: () => Promise<ApiResult<T>>,
    options?: { signup?: boolean },
  ): Promise<ApiResult<T>> => {
    if (mutating.current || pendingRef.current) {
      return { ok: false, status: 409, data: null, error: 'Finish the current account step first.' };
    }
    mutating.current = true;
    const version = ++requestVersion.current;
    setTrusted(false);
    try {
      const result = await withSessionLock(work);
      if (version !== requestVersion.current) return result;
      if (result.ok && result.data?.user) {
        const { user, recoveryCodes } = result.data;
        if (options?.signup && !lastAccountId()) associateGuestPlan(user.id);
        // A new identity must never inherit the previous profile or crew summary.
        setSession({ user });
        rememberAccountId(user.id);
        setStorageUserId(user.id);
        setKnown(true);
        if (recoveryCodes?.length) {
          pendingRef.current = true;
          setPending({ user, recoveryCodes });
        } else setTrusted(true);
        channel.current?.postMessage('changed');
      }
      return result;
    } finally {
      mutating.current = false;
      if (!pendingRef.current) void refresh();
    }
  }, [refresh]);

  const logout = useCallback(async () => {
    if (mutating.current) return { ok: false, status: 409, data: null };
    mutating.current = true;
    ++requestVersion.current;
    setTrusted(false);
    try {
      const result = await withSessionLock(() => api.logout());
      if (result.ok) {
        setSession({ user: null });
        rememberAccountId(null);
        setStorageUserId(null);
        setKnown(true);
        channel.current?.postMessage('changed');
      }
      return result;
    } finally {
      mutating.current = false;
      void refresh();
    }
  }, [refresh]);

  useEffect(() => {
    const connection = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('festival-session');
    channel.current = connection;
    if (connection) connection.onmessage = () => {
      if (mutating.current || pendingRef.current) return;
      ++requestVersion.current;
      setTrusted(false);
      // Remove old account details while the shared cookie is rechecked.
      setSession(null);
      void refresh();
    };
    const recheck = () => { if (document.visibilityState === 'visible') void refresh(); };
    document.addEventListener('visibilitychange', recheck);
    const warn = (event: BeforeUnloadEvent) => {
      if (!pendingRef.current && !mutating.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      connection?.close();
      document.removeEventListener('visibilitychange', recheck);
      window.removeEventListener('beforeunload', warn);
    };
  }, [refresh]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <SessionContext.Provider value={{ session, known, trusted, storageUserId, refresh, authenticate, logout }}>
      {pending ? (
        <div className="authpage">
          <header className="masthead">
            <h1>Save your recovery codes</h1>
            <p>Each code signs you in once and sets a new password. Write them down before continuing.</p>
          </header>
          <div className="authcard">
            <ul className="codes">{pending.recoveryCodes.map((code) => <li key={code}>{code}</li>)}</ul>
            {/* Home rather than the profile. Somebody who signed up from a
                protected screen goes back to that screen instead. */}
            <button className="primary wide" onClick={() => {
              pendingRef.current = false;
              setPending(null);
              setTrusted(true);
              navigate(safeReturnPath((location.state as { from?: unknown } | null)?.from), {
                replace: true,
                state: { accountReady: true },
              });
              void refresh();
            }}>I have written these down</button>
          </div>
        </div>
      ) : children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession used outside the session provider');
  return value;
}
